import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, blobApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { money } from "@/lib/format";
import { RefreshCw, Radar, ArrowRight, MapPin, Mail, CheckCircle2, Trash2, FileDown, FileCheck2 } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import FollowUpEmailDialog from "@/components/chat/FollowUpEmailDialog";
import DeleteQuoteDialog from "@/components/DeleteQuoteDialog";
import ResyncBolDialog from "@/components/workspace/ResyncBolDialog";
import { useAuth } from "@/context/AuthContext";

const FILTERS = ["Active", "Delivered", "All"];

const isDelivered = (s) => !!s.tracking?.final && (s.tracking?.status || "").toLowerCase().includes("deliver");

function statusClasses(s) {
  const t = (s.tracking?.status || "").toLowerCase();
  if (s.tracking?.final && t.includes("deliver")) return "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]";
  if (t.includes("cancel")) return "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))]";
  if (t.includes("out for delivery")) return "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]";
  if (!s.tracking?.status || t.includes("no carrier") || t.includes("unavailable") || t.includes("disabled"))
    return "bg-secondary text-muted-foreground";
  return "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]";
}

function dateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 10) || "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function timeShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export default function Shipments() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("Active");
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [followUpId, setFollowUpId] = useState(null);
  const [delShipment, setDelShipment] = useState(null);
  const [resyncTarget, setResyncTarget] = useState(null);
  const navigate = useNavigate();
  const timer = useRef(null);

  const openBol = async (quoteId, path) => {
    try {
      const res = await api.get(`/quotes/${quoteId}/${path}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(await blobApiError(err));
    }
  };

  const syncTrifecta = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/shipments/sync-trifecta-bols");
      if (data.reason === "no_creds") {
        toast.error(data.message || "Add CarrierRate portal credentials in Integrations → GlobalTranz first.");
      } else if (data.failed?.length) {
        const names = data.failed.map((f) => f.quote_number).slice(0, 5).join(", ");
        toast.warning(`Synced ${data.synced}/${data.total} Trifecta BOLs. ${data.failed.length} couldn't be pulled: ${names}${data.failed.length > 5 ? "…" : ""}`);
      } else {
        toast.success(`All ${data.synced} Trifecta BOL(s) now point to the real portal document.`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const refresh = async (silent = false) => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/shipments/refresh");
      setRows(data.shipments);
      setLastRefresh(new Date());
      if (!silent) toast.success(`Tracking refreshed for ${data.refreshed} shipment(s).`);
    } catch (e) {
      if (!silent) toast.error(apiError(e));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    api.get("/shipments").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
    refresh(true);
    timer.current = setInterval(() => refresh(true), 120000);
    return () => clearInterval(timer.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = rows.filter((s) => {
    if (filter === "All") return true;
    return filter === "Delivered" ? isDelivered(s) : !isDelivered(s);
  });

  return (
    <div className="space-y-5 animate-fade-up" data-testid="shipments-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">Shipments</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every booked load — R+L &amp; GlobalTranz — with live tracking. Auto-refreshes every 2 minutes
            {lastRefresh ? ` · last checked ${timeShort(lastRefresh)}` : ""}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border p-0.5">
            {FILTERS.map((f) => (
              <button key={f} data-testid={`shipments-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)}
                className={`rounded px-3 py-1.5 text-xs font-semibold transition-colors ${filter === f ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground hover:text-foreground"}`}>
                {f}
              </button>
            ))}
          </div>
          <Button variant="outline" onClick={() => refresh(false)} disabled={refreshing} data-testid="shipments-refresh" className="gap-1.5">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh Tracking
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={syncTrifecta} disabled={syncing} data-testid="shipments-sync-trifecta" className="gap-1.5"
              title="Re-pull the real Trifecta BOL (with NMFC) from the CarrierRate portal for every booking and replace any stale copies.">
              <FileCheck2 className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync Trifecta BOLs"}
            </Button>
          )}
        </div>
      </div>

      <div className="border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead>Quote</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Lane</TableHead>
              <TableHead>Carrier</TableHead>
              <TableHead>BOL</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Est. Delivery</TableHead>
              <TableHead className="text-right">Sell</TableHead>
              <TableHead className="text-center">Follow-up</TableHead>
              {isAdmin && <TableHead className="text-center">Del</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={isAdmin ? 11 : 10} className="py-12 text-center text-muted-foreground">
                <Radar className="mx-auto mb-2 h-8 w-8 opacity-40" />
                {rows.length === 0 ? "No booked shipments yet — book a load via GlobalTranz or generate an R+L BOL." : `No ${filter.toLowerCase()} shipments.`}
              </TableCell></TableRow>
            )}
            {filtered.map((s) => (
              <TableRow key={s.quote_id} data-testid={`shipment-row-${s.quote_number}`}
                onClick={() => navigate(`/quotes/${s.quote_id}`, { state: { from: "/shipments" } })}
                className="cursor-pointer border-border transition-colors hover:bg-secondary/50">
                <TableCell className="font-semibold">{s.quote_number}</TableCell>
                <TableCell className="max-w-[160px] truncate text-sm">{s.customer_name}</TableCell>
                <TableCell className="whitespace-nowrap text-sm">
                  <span className="metric-num">{s.origin_zip}</span>
                  <ArrowRight className="mx-1 inline h-3 w-3 text-muted-foreground" />
                  <span className="metric-num">{s.destination_zip}</span>
                </TableCell>
                <TableCell className="max-w-[170px] truncate text-sm">{s.carrier}</TableCell>
                <TableCell>
                  {s.bol_number ? (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs">
                      <Badge variant="outline" className={`border-0 px-1.5 text-[10px] font-bold ${s.bol_type === "GlobalTranz" ? "bg-[#00529B]/15 text-[#4d9fe8]" : "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"}`}>
                        {s.bol_type === "GlobalTranz" ? "GTZ" : "R+L"}
                      </Badge>
                      <span className="metric-num">{s.bol_number}</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`bol-download-${s.quote_number}`}
                            title="Download BOL"
                            className="ml-0.5 inline-flex items-center rounded p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                          >
                            <FileDown className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="min-w-[190px]">
                          {s.bol_type === "GlobalTranz" ? (
                            <DropdownMenuItem onClick={() => openBol(s.quote_id, "gtz-carrier-bol/pdf")} data-testid={`bol-trifecta-${s.quote_number}`}>
                              Trifecta Transport BOL
                            </DropdownMenuItem>
                          ) : (
                            <>
                              <DropdownMenuItem onClick={() => openBol(s.quote_id, "bol/pdf")} data-testid={`bol-rl-${s.quote_number}`}>
                                R+L BOL
                              </DropdownMenuItem>
                              {!isDelivered(s) && (
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); setResyncTarget({ quoteId: s.quote_id, quoteNumber: s.quote_number, proNumber: s.bol_number }); }}
                                  data-testid={`bol-modify-${s.quote_number}`}>
                                  Modify BOL
                                </DropdownMenuItem>
                              )}
                            </>
                          )}
                          {s.bol_type === "GlobalTranz" && (
                            <DropdownMenuItem onClick={() => openBol(s.quote_id, "gtz-pod/pdf")} data-testid={`pod-trifecta-${s.quote_number}`}>
                              Signed POD (Trifecta)
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </span>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${statusClasses(s)}`} title={s.tracking?.error ? `Carrier error: ${s.tracking.error}` : (s.tracking?.last_event ? `${s.tracking.last_event.status || ""} · ${s.tracking.last_event.time || ""}` : "")}>
                    {s.tracking?.status || "Not checked"}
                  </span>
                  {s.tracking?.error ? (
                    <span className="ml-1 cursor-help align-middle text-[11px] text-[hsl(var(--warning))]" data-testid="tracking-error-info" title={`Carrier error: ${s.tracking.error}`}>&#9432;</span>
                  ) : null}
                </TableCell>
                <TableCell className="max-w-[170px] truncate text-xs text-muted-foreground">
                  {s.tracking?.current_location ? (<span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.tracking.current_location}</span>) : "—"}
                </TableCell>
                <TableCell className="whitespace-nowrap text-sm">{dateShort(s.tracking?.delivery_date || s.estimated_delivery)}</TableCell>
                <TableCell className="metric-num text-right text-sm">{s.sell_price != null ? money(s.sell_price) : "—"}</TableCell>
                <TableCell className="text-center">
                  {!isDelivered(s) ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : s.followup_sent_at ? (
                    <span
                      className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground"
                      title={`Follow-up sent to ${s.followup_sent_to || "customer"} on ${dateShort(s.followup_sent_at)}`}
                      data-testid={`followup-sent-${s.quote_number}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> Sent
                    </span>
                  ) : (
                    <Button
                      size="sm" variant="outline"
                      onClick={(e) => { e.stopPropagation(); setFollowUpId(s.quote_id); }}
                      data-testid={`followup-btn-${s.quote_number}`}
                      className="h-7 gap-1 border-[hsl(var(--success))]/50 px-2 text-xs font-semibold text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/10 hover:text-[hsl(var(--success))]"
                      title='Send the "How did we do?" follow-up email'
                    >
                      <Mail className="h-3.5 w-3.5" /> Follow up
                    </Button>
                  )}
                </TableCell>
                {isAdmin && (
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setDelShipment({ id: s.quote_id, quote_number: s.quote_number, booked: true })}
                      data-testid={`shipment-delete-${s.quote_number}`}
                      title="Delete shipment (admin — does NOT cancel carrier dispatch)"
                      className="inline-flex items-center rounded px-1.5 py-1 text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-500"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <FollowUpEmailDialog
        quoteId={followUpId}
        open={!!followUpId}
        onOpenChange={(o) => {
          if (!o) {
            setFollowUpId(null);
            api.get("/shipments").then((r) => setRows(r.data)).catch(() => {});
          }
        }}
      />
      <DeleteQuoteDialog
        quote={delShipment}
        open={!!delShipment}
        onOpenChange={(v) => { if (!v) setDelShipment(null); }}
        onDeleted={(q) => setRows((rs) => rs.filter((x) => x.quote_id !== q.id))}
      />
      <ResyncBolDialog
        open={!!resyncTarget}
        onOpenChange={(v) => { if (!v) setResyncTarget(null); }}
        quoteId={resyncTarget?.quoteId}
        quoteNumber={resyncTarget?.quoteNumber}
        proNumber={resyncTarget?.proNumber}
        onSynced={() => { setResyncTarget(null); refresh(true); }}
      />
    </div>
  );
}
